import './History.css';
import Title from 'antd/es/typography/Title';
import Navbar from '../../components/Navbar/Navbar';
import { Alert, Button, Card, Col, Divider, Image, message, Modal, Row, Space, Spin, Tag } from 'antd';
import { useEffect, useMemo, useRef, useState } from 'react';
import { fetchRequest } from '../../utils/Fetcher';
import { useSelector } from 'react-redux';
import { selectUserId } from '../../store/sessionSlice';
import { FormatDate } from './FormatDate';
import { AttachSignedUrls, AttachVideoSignedUrls } from "../../utils/GetSignedURL";
import { ExportSessionBundleZip } from "../../utils/ExportSessionBundle";

const PAGE_SIZE = 5;
const DETAIL_CONCURRENCY = 3;

const STATUS_COLOR = {
  ACTIVE: "processing",
  COMPLETED: "success",
  FAILED: "error",
};

const runPool = async (ids, worker, concurrency = 3) => {
  let i = 0;
  const runners = Array.from({ length: Math.min(concurrency, ids.length) }, () => (async () => {
    while (i < ids.length) {
      const id = ids[i++];
      await worker(id);
    }
  })());
  await Promise.all(runners);
};

const Home = () => {
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [loadingDesc, setLoadingDesc] = useState("");
  const [error, setError] = useState("");

  const [sessions, setSessions] = useState([]);
  const [allSessionIds, setAllSessionIds] = useState([]);
  const [cursor, setCursor] = useState(0);

  const [messageApi, contextHolder] = message.useMessage();
  const [exportingBySession, setExportingBySession] = useState({});

  const [imageModalOpen, setImageModalOpen] = useState(false);
  const [imageModalLoading, setImageModalLoading] = useState(false);
  const [imageModalTitle, setImageModalTitle] = useState("");
  const [imageModalItems, setImageModalItems] = useState([]);

  const [videoModalOpen, setVideoModalOpen] = useState(false);
  const [videoModalLoading, setVideoModalLoading] = useState(false);
  const [videoModalTitle, setVideoModalTitle] = useState("");
  const [videoModalItems, setVideoModalItems] = useState([]);

  const userId = useSelector(selectUserId);

  const sentinelRef = useRef(null);
  const loadingMoreRef = useRef(false);

  const getImageSrc = (img) => {
    if (img?.signed_url) return img.signed_url.signed_url;
    return "";
  };

  const getVideoSrc = (video) => {
    if (video?.signed_url?.signed_url) return video.signed_url.signed_url;
    if (video?.signedUrl) return video.signedUrl;
    if (video?.url) return video.url;
    if (video?.downloadUrl) return video.downloadUrl;
    if (video?.src) return video.src;
    return "";
  };

  const hasMore = useMemo(() => cursor < (allSessionIds?.length ?? 0), [cursor, allSessionIds]);

  const fetchSessionHistory = async (uid) => {
    setLoadingDesc("Fetching session history...");
    const { result } = await fetchRequest({
      method: "POST",
      path: `auth/${uid}/sessions`,
      data: JSON.stringify({
        order: "desc"
      })
    });
    return result;
  };

  const fetchSessionDetails = async (id) => {
    const { result } = await fetchRequest({
      method: "GET",
      path: `status/sessions/${id}/summary`,
    });
    return result;
  };

  const processSessionId = async (id) => {
    try {
      const details = await fetchSessionDetails(id);

      setSessions((prev) =>
        prev.map((x) =>
          x.sessionId === id
            ? {
                ...x,
                _pending: false,
                sessionId: details?.session?.id ?? id,
                data: details,
              }
            : x
        )
      );
    } catch (e) {
      console.error("detail failed:", id, e);
      setSessions((prev) =>
        prev.map((x) =>
          x.sessionId === id
            ? { ...x, _pending: false, error: e?.message ?? String(e) }
            : x
        )
      );
    }
  };

  const loadNextPage = async () => {
    if (!hasMore) return;
    if (loadingMoreRef.current) return;

    loadingMoreRef.current = true;
    setLoadingMore(true);
    setLoadingDesc("Loading more sessions...");

    const slice = allSessionIds.slice(cursor, cursor + PAGE_SIZE);

    setSessions(prev => [
      ...prev,
      ...slice.map(id => ({ sessionId: id, _pending: true }))
    ]);


    try {
      await runPool(slice, processSessionId, DETAIL_CONCURRENCY);

      setCursor((prev) => prev + slice.length);
    } finally {
      setLoadingMore(false);
      loadingMoreRef.current = false;
    }
  };

  const setExporting = (sessionId, value) => {
    setExportingBySession((prev) => ({
      ...prev,
      [sessionId]: value,
    }));
  };

  const pickLatestMergedSucceeded = (videos = []) =>
    (videos ?? [])
      .filter((v) => v?.type === "merged" && v?.state === "SUCCEEDED")
      .sort(
        (a, b) =>
          new Date(b?.finishedAt ?? b?.createdAt ?? 0) -
          new Date(a?.finishedAt ?? a?.createdAt ?? 0)
      )[0] || null;

  const handleExportSession = async (sessionSummary) => {
    const sessionId = sessionSummary?.session?.id ?? sessionSummary?.sessionId;
    const categories = sessionSummary?.categories ?? {};

    if (!sessionId) {
      messageApi.error("Session ID not found");
      return;
    }

    if (exportingBySession[sessionId]) return;

    try {
      setExporting(sessionId, true);

      const imageEntries =
        categories?.campaign_images?.items?.entries?.length
          ? categories.campaign_images.items.entries
          : categories?.images?.items?.entries ?? [];

      const signedImages = imageEntries.length
        ? await AttachSignedUrls(imageEntries, 1)
        : [];

      const videoEntries = categories?.videos?.items?.entries ?? [];
      const mergedLatest = pickLatestMergedSucceeded(videoEntries);

      let signedMergedVideo = null;
      if (mergedLatest) {
        const [signedVideo] = await AttachVideoSignedUrls([mergedLatest], 1);
        signedMergedVideo = signedVideo ?? null;
      }

      await ExportSessionBundleZip({
        categories,
        sessionId,
        generatedImages: signedImages,
        mergedVideo: signedMergedVideo,
      });

      messageApi.success("Session exported");
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || "Failed to export session");
    } finally {
      setExporting(sessionId, false);
    }
  };

  const handleOpenImages = async (sessionSummary) => {
    const sessionId = sessionSummary?.session?.id ?? sessionSummary?.sessionId;
    const categories = sessionSummary?.categories ?? {};

    try {
      setImageModalLoading(true);
      setImageModalTitle(`Campaign Images - ${sessionId ?? "-"}`);
      setImageModalItems([]);

      const imageEntries =
        categories?.campaign_images?.items?.entries?.length
          ? categories.campaign_images.items.entries
          : categories?.images?.items?.entries ?? [];

      if (!imageEntries.length) {
        setImageModalItems([]);
        setImageModalOpen(true);
        return;
      }

      const signedImages = await AttachSignedUrls(imageEntries, 1);
      setImageModalItems(signedImages ?? []);
      setImageModalOpen(true);
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || "Failed to load images");
    } finally {
      setImageModalLoading(false);
    }
  };

  const handleOpenVideos = async (sessionSummary) => {
    const sessionId = sessionSummary?.session?.id ?? sessionSummary?.sessionId;
    const categories = sessionSummary?.categories ?? {};

    try {
      setVideoModalLoading(true);
      setVideoModalTitle(`Videos - ${sessionId ?? "-"}`);
      setVideoModalItems([]);

      const videoEntries = categories?.videos?.items?.entries ?? [];
      const mergedLatest = pickLatestMergedSucceeded(videoEntries);

      if (!mergedLatest) {
        setVideoModalItems([]);
        setVideoModalOpen(true);
        return;
      }

      const [signedVideo] = await AttachVideoSignedUrls([mergedLatest], 1);
      setVideoModalItems(signedVideo ? [signedVideo] : []);
      setVideoModalOpen(true);
    } catch (e) {
      console.error(e);
      messageApi.error(e?.message || "Failed to load videos");
    } finally {
      setVideoModalLoading(false);
    }
  };

  useEffect(() => {
    let cancelled = false;

    const init = async () => {
      if (!userId) return;

      try {
        setLoading(true);
        setError("");
        setSessions([]);
        setAllSessionIds([]);
        setCursor(0);

        const sessionSummary = await fetchSessionHistory(userId);
        const sessionList = sessionSummary?.sessions ?? [];
        const ids = sessionList.map((s) => s?.sessionId).filter(Boolean);

        if (cancelled) return;

        setAllSessionIds(ids);

        queueMicrotask(() => {
          if (!cancelled) loadNextPage();
        });
      } catch (e) {
        if (!cancelled) setError(e?.message ?? String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    init();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [userId]);

  // infinite scroll via IntersectionObserver
  useEffect(() => {
    if (!sentinelRef.current) return;

    const el = sentinelRef.current;

    const obs = new IntersectionObserver(
      (entries) => {
        const first = entries[0];
        if (first?.isIntersecting) {
          loadNextPage();
        }
      },
      { root: null, rootMargin: "200px", threshold: 0.01 }
    );

    obs.observe(el);
    return () => obs.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sentinelRef.current, hasMore, cursor, allSessionIds]);

  return (
    <div>
      {contextHolder}
      <Navbar />
      <div className='history-page-container'>
        <Title level={2} className='history-title'>History</Title>

        {!!error && (
          <Alert
            style={{ marginBottom: 12 }}
            type="error"
            showIcon
            title="Failed to load history"
            description={error}
          />
        )}

        <div className="history-scroll-area">
          <Space orientation="vertical" size={12} className='space-container'>
            {(sessions ?? []).map((s, idx) => {
              const summary = s?.data;
              const session = summary?.session;
              const categories = summary?.categories;

              const sessionId = session?.id ?? s?.sessionId ?? `row-${idx}`;
              const createdAt = FormatDate(session?.createdAt ?? "-");

              const mediaStatus = session?.status ?? "UNKNOWN";
              const status = String(mediaStatus).toUpperCase();
              const tagColor = STATUS_COLOR[status] ?? "default";

              const pickSelected = (cat) => {
                const entries = cat?.items?.entries ?? [];
                const selected = new Set(cat?.items?.selectedIds ?? []);
                return entries.filter((e) => selected.has(e?.id));
              };

              const persona = pickSelected(categories?.customer_personas)?.[0] ?? null;
              const concepts = pickSelected(categories?.campaign_concepts) ?? [];

              const campaignImagesEntries = categories?.campaign_images?.items?.entries ?? [];
              const campaignImageCount = campaignImagesEntries.length;

              const videoEntries = categories?.videos?.items?.entries ?? [];
              const mergedLatest = pickLatestMergedSucceeded(videoEntries);
              const hasMergedVideo = !!mergedLatest;

              return (
                <Card key={sessionId} className="session-card">
                  <Row justify="space-between" align="middle">
                    <Col>
                      <div className="session-title">
                        Session <span className="muted">{sessionId}</span>
                      </div>
                      <div className="muted">{createdAt}</div>
                    </Col>
                    <Col className="session-actions">
                      <Tag color={tagColor} className="status-pill">
                        {String(mediaStatus)}
                      </Tag>
                      <Button
                        className="action-btn"
                        onClick={() => handleExportSession(summary)}
                        loading={!!exportingBySession[sessionId]}
                        disabled={!summary || !!exportingBySession[sessionId] || s?._pending}
                      >
                        Export
                      </Button>
                    </Col>
                  </Row>

                  <Divider style={{ margin: "12px 0" }} />

                  <Row gutter={[24, 12]}>
                    <Col xs={24} md={10} className='row-content-summary'>
                      {persona &&
                        <>
                          <div className="label">Customer persona</div>
                          <div className="value">{persona?.persona_name ?? "-"}</div>
                          <div className="muted">{persona?.persona_bio ?? "-"}</div>
                        </>
                      }
                    </Col>

                    <Col xs={24} md={10} className='row-content-summary'>
                       {concepts.length > 0 && (
                        <>
                          <div className="label">Campaign concepts</div>
                          <div className="concept-list">
                            {concepts.map((c, i) => (
                              <div key={c?.id ?? i} className="concept-item">
                                <Tag>{c?.title}</Tag>
                                <div className="value">{c?.tagline ?? "-"}</div>
                                <div className="muted">{c?.emotional_hook ?? "-"}</div>
                              </div>
                            ))}
                          </div>
                        </>
                      )}
                    </Col>

                    <Col xs={24} md={4} className='row-content-summary'>
                      {campaignImageCount > 0 && (
                        <>
                          <div className="label">Campaign Image</div>
                          <div className="value">{campaignImageCount} images</div>
                          <Button
                            className="link-button"
                            type="link"
                            onClick={() => handleOpenImages(summary)}
                            disabled={!summary || s?._pending}
                          >
                            Open Images
                          </Button>
                        </>
                      )}

                      {hasMergedVideo && (
                        <>
                          <div className="label" style={{ marginTop: 12 }}>Video</div>
                          <div className="value">1 merged video</div>
                          <Button
                            className='link-button'
                            type="link"
                            onClick={() => handleOpenVideos(summary)}
                            disabled={!summary || s?._pending}
                          >
                            Open Video
                          </Button>
                        </>
                      )}
                    </Col>
                  </Row>

                  {!!s?.error && (
                    <div style={{ marginTop: 12 }}>
                      <Alert type="warning" showIcon message="Session detail failed" description={s.error} />
                    </div>
                  )}
                </Card>
              );
            })}

            <div ref={sentinelRef} style={{ height: 1 }} />

            {!loading && !error && hasMore && (
              <div style={{ textAlign: "center", padding: 12 }}>
                <Button onClick={loadNextPage} disabled={loadingMore}>
                  {loadingMore ? <Spin size="small" /> : "Load more"}
                </Button>
              </div>
            )}

            {(loading || loadingMore) && (
              <div className='loading-container'>
                {loadingDesc}
                <Spin />
              </div>
            )}

            {!loading && !error && !hasMore && (allSessionIds?.length ?? 0) > 0 && (
              <div style={{ textAlign: "center", padding: 12 }} className="muted">
                End of history
              </div>
            )}
          </Space>
        </div>
      </div>

      <Modal
        title={imageModalTitle}
        open={imageModalOpen}
        onCancel={() => setImageModalOpen(false)}
        footer={null}
        width={1000}
      >
        {imageModalLoading ? (
          <div className='loading-container'>
            Loading images...
            <Spin />
          </div>
        ) : (
          <Image.PreviewGroup>
            <div className="history-image-strip">
              {imageModalItems.map((img, idx) => {
                const src = getImageSrc(img);
                const key = img?.id || idx;
                const alt = img?.fileName || `image-${idx}`;

                return (
                  <div className="history-image-slide" key={key}>
                    <Image
                      alt={alt}
                      src={src}
                      className="history-image-preview"
                    />
                  </div>
                );
              })}
            </div>
          </Image.PreviewGroup>
        )}
      </Modal>

      <Modal
        title={videoModalTitle}
        open={videoModalOpen}
        onCancel={() => setVideoModalOpen(false)}
        footer={null}
        width={1000}
      >
        {videoModalLoading ? (
          <div className='loading-container'>
            Loading videos...
            <Spin />
          </div>
        ) : (
          <div className="history-video-list">
            {videoModalItems.map((video, idx) => {
              const src = getVideoSrc(video);
              const key = video?.id || idx;

              return (
                <div className="history-video-item" key={key}>
                  <video
                    src={src}
                    controls
                    className="history-video-preview"
                  />
                </div>
              );
            })}
          </div>
        )}
      </Modal>
    </div>
  );
};

export default Home;
